const translationWarning =
  'The attempt to fetch labels has not been run. Please ensure you have imported and run fetchAndLoadLabels from toolkit-utils'
let LABELS = {}
let userLocale = ''
let hasBeenWarned = false

/** Updated version of i18n
 * A utility function that fetches the labels for a specific
 * project and saves it as LABELS for later use. This is usually
 * used to translations.
 *
 * This function should be called before any render method in your
 * application, and rendering should be dependent on it successfully
 * returning the labels object.
 *
 * instead of fetching from aemServices, this gets labels like
 * located next to js file
 *
 * @param  {String} app              The name of the application
 * @param  {String} labelFileName    The name of the application labels file
 * @param  {String} [locale='en_US'] The users locale, typically window.Insight.locale
 * @param  {String} [language='en']  The users language, typically window.Insight.language
 * @param  {Bool}   isDebranded      Controls debranding of strings
 */

export function normalizeLocaleForLabels(locale) {
  const EMEACountries = [
    'at',
    'be',
    'dk',
    'fr',
    'de',
    'ie',
    'it',
    'nl',
    'no',
    'es',
    'se',
    'ch',
    'gb',
  ]
  const localeDetails = locale.split('_')
  const localeCountry = localeDetails[1].toLowerCase()
  const localeLang = localeDetails[0].toLowerCase()
  const isEMEALocale = EMEACountries.includes(localeCountry)
  if (isEMEALocale) {
    return `${localeLang}_GB`
  }
  return locale
}

export function fetchLabels({
  app,
  labelFileName,
  locale = 'en_US',
  isDebranded,
  migrated = false,
}) {
  userLocale = locale
  const nextLocale = normalizeLocaleForLabels(locale)
  let url = `/etc/clientlibs/insight-ui/${app}/labels/${labelFileName}.${nextLocale}.json`
  let debrandUrl = `/etc/clientlibs/insight-ui/${app}/labels/${labelFileName}-debrand.${nextLocale}.json`
  if (migrated) {
    url = `/insight-ui/${app}/labels/${labelFileName}.${nextLocale}.json`
    debrandUrl = `/insight-ui/${app}/labels/${labelFileName}-debrand.${nextLocale}.json`
  }
  return Promise.all([
    fetch(url, { cache: 'default' }).then((response) => response.json()),
    isDebranded
      ? fetch(debrandUrl, { cache: 'default' }).then((response) =>
          response.json()
        )
      : Promise.resolve(false),
  ])
    .then(([regular, debranded]) => {
      hasBeenWarned = true
      if (isDebranded && !debranded) {
        console.warn('No debranding labels exist for the specified app')
      }
      setLabels(
        Object.assign(
          regular.labels || regular,
          debranded.labels || debranded || {}
        )
      )
      return LABELS
    })
    .catch((error) => {
      hasBeenWarned = true
      console.warn(`Failed to fetch translations:\n ${error}`)
      throw error
    })
}

const buildUrl = (sourceLabel, locale, normalizeLocale, migrated = false) => {
  const { app, labelFileName, legacy } = sourceLabel
  if (labelFileName && !migrated) {
    return `/etc/clientlibs/insight-ui/${app}/labels/${labelFileName}.${normalizeLocale}.json`
  } else {
    return `/insight-ui/${app}/labels/${labelFileName}.${normalizeLocale}.json`
  }
  return legacy
    ? `/insightweb/assets/labels/${app}.${locale}.json`
    : `/aemServices/labels.${app}.${locale}.json`
}

export function fetchMultipleLabels(
  sources = [],
  locale = 'en_US',
  isDebranded
) {
  userLocale = locale
  const nextLocale = normalizeLocaleForLabels(locale)

  if (sources.length) {
    let urls = sources.map((source) => buildUrl(source, locale, nextLocale))
    if (isDebranded) {
      const debrandedUrls = sources.map((source) =>
        buildUrl(`${source}-debrand`, locale, nextLocale)
      )
      urls = urls.concat(debrandedUrls)
    }
    return Promise.all(
      urls.map((url) =>
        fetch(url, { cache: 'default' }).then((response) => response.json())
      )
    )
      .then((responses) => {
        hasBeenWarned = true
        const labels = responses.reduce((acc, response) => {
          return Object.assign(acc, response.labels || response)
        }, {})
        setLabels(labels)
        return LABELS
      })
      .catch((error) => {
        hasBeenWarned = true
        console.warn(`Failed to fetch translations:\n ${error}`)
        throw error
      })
  }
}

/** Updated version of fetchAndLoadLabels compatible with EMEA
 * A utility function that fetches the labels for a specific
 * project and saves it as LABELS for later use. This is usually
 * used to translations.
 *
 * This function should be called before any render method in your
 * application, and rendering should be dependent on it successfully
 * returning the labels object.
 *
 * @param  {String} app              The name of the application
 * @param  {String} [locale='en_US'] The users locale, typically window.Insight.locale
 * @param  {String} [language='en']  The users language, typically window.Insight.language
 * @param  {Bool}   isDebranded      Controls debranding of strings
 */
export function i18n({ app, locale = 'en_US', isDebranded }) {
  userLocale = locale
  const url = `/aemServices/labels.${app}.${locale}.json`
  const debrandUrl = `/aemServices/labels.${app}-debrand.${locale}.json`
  return Promise.all([
    fetch(url, { cache: 'default' }).then((response) => response.json()),
    isDebranded
      ? fetch(debrandUrl, { cache: 'default' }).then((response) =>
          response.json()
        )
      : Promise.resolve(false),
  ])
    .then(([regular, debranded]) => {
      hasBeenWarned = true
      if (isDebranded && !debranded) {
        console.warn('No debranding labels exist for the specified app')
      }
      setLabels(
        Object.assign(
          regular.labels || regular,
          debranded.labels || debranded || {}
        )
      )
      return LABELS
    })
    .catch((error) => {
      hasBeenWarned = true
      console.warn(`Failed to fetch translations:\n ${error}`)
      throw error
    })
}

/**
 * Check to see if LABELS is empty.
 * This is used by external library such as toolkit-react to check if it has an empty cache.  It will be empty when the library and application resolve
 * to 2 different versions of toolkit-utils.  We will then use setLabels to make sure the library points to application's labels object reference returned by i18n function.
 * This resolves translation issues for toolkit components.
 */
export function hasLabels() {
  return Object.keys(LABELS).length > 0
}
/**
 * Sets LABELS cache from passed in content object
 * @param  {Object} labels is passed
 */
export function setLabels(labels) {
  LABELS = labels
}

/**
 * Fetch all labels related to output email header & footer for both NA/EMEA
 * @param  {String} country is passed
 * @return {String} country specific labels
 */
export function getConfigLabels(country) {
  const configsUrl = window?.flags['GNA-16081-Config-Data-Service']
    ? '/insightweb/config/company'
    : '/aemServices/config.company-configs.json'
  return fetch(configsUrl)
    .then((response) => response.json())
    .then((data) => {
      const labels = Object.assign(data.labels || data || {})
      return labels[country]
    })
}

/**
 * Translates a string using the LABELS object
 * @param  {String} key English text used as a key
 * @param  {String} optKey Optional second key
 * @return {String}     Translated text
 */
export function t(key, optKey) {
  warn()
  /*
  TODO: Implement logging of missing labels if/when back-end writes an API
   */
  return LABELS[key] || (optKey && LABELS[optKey]) || key
}

/**
 * Returns the locale saved from fetchAndLoadLabels
 * @return {String} The saved locale
 */
export function l() {
  warn()
  return userLocale
}

/**
 * Warns the developer if they are using the t or l functions
 * without running fetchAndLoadLabels first.
 */
function warn() {
  if (!hasBeenWarned) {
    console.warn(translationWarning)
    hasBeenWarned = true
  }
}
